import type {
  Availability,
  Booking,
  Catalog,
  ConfirmResult,
  DepositStatus,
  DepositGatewayCode,
  Hold,
  RequestCodeResult,
  SessionResult,
  Site,
  Slots,
  Staff,
  Account,
} from './types';

/**
 * Transport for the public booking API.
 *
 * Deliberately plain fetch with no interceptors: the portal has exactly one
 * kind of credential (the customer token), one base path, and one error shape,
 * so a shared axios factory would add indirection without buying anything.
 *
 * The API base is empty in dev (Vite proxies /api → :3004) and set to the API
 * origin in production via VITE_API_BASE_URL, matching the other apps.
 */

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export class BookingApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly params?: Record<string, string | number>,
  ) {
    super(message);
    this.name = 'BookingApiError';
  }
}

let authToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(
  slug: string,
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const { auth, ...rest } = init;
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...(rest.body ? { 'content-type': 'application/json' } : {}),
    ...(rest.headers as Record<string, string> | undefined),
  };
  if (auth && authToken) headers.authorization = `Bearer ${authToken}`;

  const res = await fetch(`${API_BASE}/api/v1/booking/${encodeURIComponent(slug)}${path}`, {
    ...rest,
    headers,
  });

  if (res.status === 204) return undefined as T;

  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const envelope = (body ?? {}) as { error?: string; message?: string; params?: Record<string, string | number> };
    if (res.status === 401 && auth) onUnauthorized?.();
    throw new BookingApiError(
      res.status,
      envelope.error ?? `HTTP_${res.status}`,
      envelope.message ?? 'Something went wrong. Please try again.',
      envelope.params,
    );
  }
  return body as T;
}

const qs = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

export const bookingApi = {
  site: (slug: string, subsidiaryId?: number): Promise<Site> =>
    request<Site>(slug, `/site${qs({ subsidiary_id: subsidiaryId })}`),

  catalog: (slug: string, branchId: number, subsidiaryId?: number): Promise<Catalog> =>
    request<Catalog>(slug, `/services${qs({ branch_id: branchId, subsidiary_id: subsidiaryId })}`),

  staff: async (slug: string, branchId: number, serviceIds: number[], subsidiaryId?: number): Promise<Staff[]> =>
    (
      await request<{ data: Staff[] }>(
        slug,
        `/staff${qs({
          branch_id: branchId,
          subsidiary_id: subsidiaryId,
          service_ids: serviceIds.length ? serviceIds.join(',') : undefined,
        })}`,
      )
    ).data,

  availability: (
    slug: string,
    args: { branchId: number; serviceIds: number[]; employeeId?: number | null; from: string; to?: string; subsidiaryId?: number },
  ): Promise<Availability> =>
    request<Availability>(
      slug,
      `/availability${qs({
        branch_id: args.branchId,
        subsidiary_id: args.subsidiaryId,
        service_ids: args.serviceIds.join(','),
        employee_id: args.employeeId ?? undefined,
        from: args.from,
        to: args.to,
      })}`,
    ),

  slots: (
    slug: string,
    args: { branchId: number; serviceIds: number[]; employeeId?: number | null; date: string; subsidiaryId?: number },
  ): Promise<Slots> =>
    request<Slots>(
      slug,
      `/slots${qs({
        branch_id: args.branchId,
        subsidiary_id: args.subsidiaryId,
        service_ids: args.serviceIds.join(','),
        employee_id: args.employeeId ?? undefined,
        date: args.date,
      })}`,
    ),

  requestCode: (
    slug: string,
    body: {
      email?: string;
      phone?: string;
      name?: string;
      locale?: 'en' | 'ar';
      /** The dial-code picker's country, so a bare local number is unambiguous. */
      country_iso?: string;
      /** Only 'email', and only as the fallback after a failed delivery. */
      channel?: 'email';
    },
  ): Promise<RequestCodeResult> =>
    request<RequestCodeResult>(slug, '/auth/request-code', { method: 'POST', body: JSON.stringify(body) }),

  verifyCode: (
    slug: string,
    body: {
      email?: string;
      phone?: string;
      code: string;
      name?: string;
      // Must match what requestCode was given, or the challenge is looked up
      // under a destination no code was ever sent to.
      country_iso?: string;
      channel?: 'email';
    },
  ): Promise<SessionResult> =>
    request<SessionResult>(slug, '/auth/verify', { method: 'POST', body: JSON.stringify(body) }),

  me: (slug: string): Promise<Account> => request<Account>(slug, '/me', { auth: true }),

  updateMe: (slug: string, body: Partial<Account>): Promise<Account> =>
    request<Account>(slug, '/me', { method: 'PUT', auth: true, body: JSON.stringify(body) }),

  createHold: (
    slug: string,
    body: { branch_id: number; service_ids: number[]; employee_id?: number | null; start_at: string; subsidiary_id?: number },
  ): Promise<Hold> =>
    request<Hold>(slug, '/holds', { method: 'POST', auth: true, body: JSON.stringify(body) }),

  releaseHold: (slug: string, token: string): Promise<void> =>
    request<void>(slug, `/holds/${encodeURIComponent(token)}`, { method: 'DELETE', auth: true }),

  confirm: (
    slug: string,
    body: {
      hold_token: string;
      notes?: string;
      locale?: 'en' | 'ar';
      return_url?: string;
      /** The customer's pick among the site's `deposit_gateways`. */
      gateway?: DepositGatewayCode;
    },
  ): Promise<ConfirmResult> =>
    request<ConfirmResult>(slug, '/bookings', { method: 'POST', auth: true, body: JSON.stringify(body) }),

  bookings: async (slug: string, scope: 'upcoming' | 'past' | 'all', subsidiaryId?: number): Promise<Booking[]> =>
    (
      await request<{ data: Booking[] }>(slug, `/bookings${qs({ scope, subsidiary_id: subsidiaryId })}`, {
        auth: true,
      })
    ).data,

  booking: (slug: string, id: number): Promise<Booking> =>
    request<Booking>(slug, `/bookings/${id}`, { auth: true }),

  cancel: (slug: string, id: number, reason?: string): Promise<Booking> =>
    request<Booking>(slug, `/bookings/${id}/cancel`, {
      method: 'POST',
      auth: true,
      body: JSON.stringify({ reason }),
    }),

  reschedule: (slug: string, id: number, startAt: string, employeeId?: number | null): Promise<Booking> =>
    request<Booking>(slug, `/bookings/${id}/reschedule`, {
      method: 'POST',
      auth: true,
      body: JSON.stringify({ start_at: startAt, employee_id: employeeId ?? null }),
    }),

  deposit: (slug: string, chargeId: string): Promise<DepositStatus> =>
    request<DepositStatus>(slug, `/deposits/${encodeURIComponent(chargeId)}`, { auth: true }),

  /** The same poll, by the reference the return URL carries (`pos_ref`). */
  depositByRef: (slug: string, reference: string): Promise<DepositStatus> =>
    request<DepositStatus>(slug, `/deposits/by-ref/${encodeURIComponent(reference)}`, { auth: true }),
};
